/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-color-literals */
/* eslint-disable react-native/no-inline-styles */
/* eslint-disable react/display-name */
/*
Author: Gilang Romadhanu Tartila
*/

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { View, Dimensions } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { isWeb, isMobileWeb } from '@global/screens';
import {
  convertFullAddress,
  convertTimeOfDate,
  isVn,
  normalizeKeyword,
} from '@global/helpers';
import Colors from '@global/colors';
import {
  FormWrapper,
  FormGroup,
  Loader,
  KeyboardWrapper,
} from '@components/screen/Layout';
import ModalFull from '@components/modal/ModalFull';
import FullLoader from '@/components/global/FullLoader';
import Button from '@components/form/Button';
import DatePicker from '@components/form/DatePicker';
import TimePicker from '@components/form/TimePicker';
import SelectBox from '@components/form/SelectBox';
import RadioButton from '@components/form/RadioButton';
import InputLoading from '@components/form/InputLoading';
import SalesPersonSearch from '@components/form/search/SalesPersonSearch';
import CustomerSearch from '@components/form/search/CustomerReturnSearch';
import Input from '@components/form/Input';
import MapPicker from '@components/form/MapPicker';
import SociollaText from '@components/sociolla/SociollaText';
import Alert from '@components/global/Alert';
import Discard from '@components/global/Discard';

import useApi from '@/hooks/useApi';
import { MsB2b } from '@api/ms-b2b';
import { MsAccount } from '@api/ms-account';
import { showAlert, hideAlert, showDiscard, hideDiscard } from '@/store/global';
import { showPopup } from '@/store/modal';

const dimension = Dimensions.get('window');
const today = new Date();
const now_time = `${today.getHours()}:${today.getMinutes()}`;

const extractIds = (arr) =>
  Array.isArray(arr) ? arr.map((a) => a.id).filter(Boolean) : [];

const SalesActivityForm = ({
  show,
  activityType = 'visiting_existing_customer',
  detailData = {},
  onClose,
  selectedRole = '',
  onSubmit,
}) => {
  const dispatch = useDispatch();
  const insets = useSafeAreaInsets();
  const formRef = useRef();
  const submitLockRef = useRef(false);
  const userData = useSelector((state) => state.auth.user);
  const userRole = userData?.role?.code;
  const isEdit = detailData && Object.keys(detailData).length ? true : false;
  const isEngage = activityType == 'engage_potential_customers';

  const parseDateValue = (value) => {
    if (!value) {
      return null;
    }

    if (value instanceof Date) {
      return isNaN(value.getTime()) ? null : value;
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) {
        return null;
      }
      const normalized = trimmed.includes('T')
        ? trimmed
        : trimmed.replace(' ', 'T');
      const parsed = new Date(normalized);
      if (!isNaN(parsed.getTime())) {
        return parsed;
      }
      if (!normalized.endsWith('Z')) {
        const parsedWithZone = new Date(`${normalized}Z`);
        if (!isNaN(parsedWithZone.getTime())) {
          return parsedWithZone;
        }
      }
      const numericTimestamp = Number(trimmed);
      if (!Number.isNaN(numericTimestamp)) {
        const parsedFromNumber = new Date(numericTimestamp);
        if (!isNaN(parsedFromNumber.getTime())) {
          return parsedFromNumber;
        }
      }
      return null;
    }

    if (typeof value === 'number') {
      const parsed = new Date(value);
      return isNaN(parsed.getTime()) ? null : parsed;
    }

    return null;
  };

  const withMongoId = (entity) => {
    if (!entity || typeof entity !== 'object') {
      return entity;
    }
    const idValue = entity._id || entity.id;
    if (!idValue) {
      return { ...entity };
    }
    return {
      ...entity,
      _id: idValue,
      id: idValue,
    };
  };

  const normalizedDetailData = useMemo(() => {
    if (!isEdit) {
      return detailData;
    }

    const customer =
      detailData?.customer && Object.keys(detailData.customer).length
        ? {
            ...withMongoId(detailData.customer),
            ...(detailData.customer?.address
              ? { address: withMongoId(detailData.customer?.address) }
              : {}),
          }
        : detailData?.customer;

    const outlet = detailData?.outlet
      ? withMongoId(detailData.outlet)
      : detailData?.outlet;

    return {
      ...detailData,
      ...(customer ? { customer } : {}),
      ...(outlet ? { outlet } : {}),
    };
  }, [detailData, isEdit]);

  const [isLoading, setIsLoading] = useState(false);
  const [loadingCustomer, setLoadingCustomer] = useState(false);
  const [filterCustomer, setFilterCustomer] = useState([]);
  const [disabled, setDisabled] = useState(false);
  const [hasDeliveryChanged, setHasDeliveryChanged] = useState(false);

  const getCustomerDetail = useApi(MsAccount.getCustomerId);
  const getOutlets = MsAccount.getOutletsByCustomerId;

  const [addresses, setAddresses] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [isSubDistributor, setIsSubDistributor] = useState(false);
  const initialAddressType = useMemo(() => {
    const type =
      normalizedDetailData?.customer?.address?.type ||
      normalizedDetailData?.customer_address?.type;
    if (isEdit && type && typeof type === 'string') {
      return type;
    }
    return '';
  }, [isEdit, normalizedDetailData]);
  const [addressType, setAddressType] = useState(initialAddressType);
  const [listAddressType, setListAddressType] = useState({
    delivery: [],
    store: [],
  });
  const [listAddress, setListAddress] = useState([]);
  const [addressSearchKeyword, setAddressSearchKeyword] = useState('');
  const [outletSearchKeyword, setOutletSearchKeyword] = useState('');

  const getAddressSearchText = (address) =>
    [address?.name, address?.city?.name].filter(Boolean).join(' ');

  const filteredListAddress = useMemo(() => {
    if (!isVn() || !addressSearchKeyword) {
      return listAddress;
    }

    const kw = normalizeKeyword(addressSearchKeyword);

    return (Array.isArray(listAddress) ? listAddress : []).filter((addr) =>
      normalizeKeyword(getAddressSearchText(addr)).includes(kw),
    );
  }, [listAddress, addressSearchKeyword]);

  const filteredOutlets = useMemo(() => {
    if (!isVn() || !outletSearchKeyword) {
      return outlets;
    }

    const kw = normalizeKeyword(outletSearchKeyword);

    return (Array.isArray(outlets) ? outlets : []).filter((outlet) =>
      normalizeKeyword(getAddressSearchText(outlet)).includes(kw),
    );
  }, [outlets, outletSearchKeyword]);

  const isSalesman = userRole == 'salesman' ? true : false;
  const isBeautyAdvisor = userRole == 'beauty_advisor' ? true : false;
  const isMerchandiser = userRole == 'merchandiser' ? true : false;
  const isPromotor = userRole == 'promotor' ? true : false;
  const isCollector = userRole == 'collector' ? true : false;
  const roleLabel =
    selectedRole === 'beauty_advisor'
      ? 'Beauty Advisor'
      : selectedRole === 'salesman'
      ? 'Salesman'
      : selectedRole === 'merchandiser'
      ? 'Merchandiser'
      : selectedRole === 'promotor'
      ? 'Promotor'
      : selectedRole === 'collector'
      ? 'Collector'
      : isBeautyAdvisor
      ? 'Beauty Advisor'
      : isMerchandiser
      ? 'Merchandiser'
      : isPromotor
      ? 'Promotor'
      : isCollector
      ? 'Collector'
      : 'Sales Person';
  const prefix_title =
    activityType == 'visiting_existing_customer'
      ? 'Visit'
      : activityType == 'invoice_collection'
      ? 'Invoice Collection'
      : activityType == 'event_activity'
      ? 'Event Activity'
      : 'Engage Potential Customer';
  const initialValues = {
    sales_person:
      isSalesman ||
      isBeautyAdvisor ||
      isMerchandiser ||
      isPromotor ||
      isCollector
        ? { id: userData._id, name: userData.name }
        : isEdit &&
          normalizedDetailData?.user &&
          Object.keys(normalizedDetailData.user).length
        ? normalizedDetailData.user
        : '',
    customer:
      isEdit && normalizedDetailData?.customer
        ? normalizedDetailData.customer
        : { name: '' },
    customer_address:
      isEdit &&
      normalizedDetailData?.customer &&
      Object.keys(normalizedDetailData.customer).length
        ? normalizedDetailData.customer.address
        : '',
    date:
      isEdit && normalizedDetailData?.visited_at
        ? parseDateValue(normalizedDetailData.visited_at) ||
          normalizedDetailData.visited_at
        : '',
    finished_at:
      isEdit && normalizedDetailData?.finished_at
        ? normalizedDetailData.finished_at
        : '',
    start_time:
      isEdit && normalizedDetailData?.start_time
        ? normalizedDetailData.start_time
        : '',
    end_time:
      isEdit && normalizedDetailData?.end_time
        ? normalizedDetailData.end_time
        : '',
    notes:
      isEdit && normalizedDetailData?.notes ? normalizedDetailData.notes : '',
    event_name:
      isEdit && normalizedDetailData?.event_name
        ? normalizedDetailData.event_name
        : '',
    status:
      isEdit && normalizedDetailData?.status
        ? normalizedDetailData.status
        : 'draft',
    ...(activityType === 'visiting_existing_customer' ||
    activityType === 'invoice_collection'
      ? {
          outlet:
            isEdit && normalizedDetailData?.outlet
              ? normalizedDetailData.outlet
              : { name: '' },
        }
      : {}),
  };

  const addressTypeOptions = [
    {
      label: 'Delivery Address',
      value: 'delivery',
    },
    {
      label: 'Store Address',
      value: 'store',
    },
  ];

  useEffect(() => {
    const addresses = listAddressType[addressType] || [];
    setListAddress(addresses);
    setAddressSearchKeyword('');
    onLocationChanged();
  }, [addressType, listAddressType]);

  useEffect(() => {
    setAddressType((prev) =>
      prev === initialAddressType ? prev : initialAddressType,
    );
  }, [initialAddressType]);

  const filter_person = {};
  const filter_cus = {
    status: 'approved',
  };
  if (filterCustomer?.length) {
    if (
      ['beauty_advisor', 'merchandiser'].includes(
        formRef.current?.values?.sales_person?.role,
      ) ||
      ['beauty_advisor', 'merchandiser'].includes(
        formRef.current?.values?.sales_person?.role?.code,
      )
    ) {
      const role = formRef.current.values.sales_person?.role?.code
        ? formRef.current.values.sales_person.role.code
        : formRef.current.values.sales_person.role;
      const key = `${role}.id`;
      filter_cus[key] = formRef.current.values.sales_person.id;
    } else {
      filter_cus['$or'] = [
        { 'sales_team.id': { $in: filterCustomer } },
        { 'market_type.code': 'mt' },
        { 'market_type.code': 'MT' },
      ];
    }
  }

  const userSalesTeam = extractIds(userData?.sales_team);

  if (userSalesTeam?.length) {
    filter_person.remove_default_filter = true;
    if (userRole == 'sales_supervisor') {
      filter_person.sales_team = { $elemMatch: { id: { $in: userSalesTeam } } };
      if (selectedRole == 'beauty_advisor') {
        filter_person['role.code'] = 'beauty_advisor';
      } else if (selectedRole == 'merchandiser') {
        filter_person['role.code'] = 'merchandiser';
      } else {
        filter_person['role.code'] = 'salesman';
        filter_person.sales_code = { $exists: true };
      }
    } else if (['branch_head', 'finance_supervisor'].includes(userRole)) {
      if (selectedRole == 'beauty_advisor') {
        filter_person['role.code'] = 'beauty_advisor';
        filter_person.sales_team = {
          $elemMatch: { id: { $in: userSalesTeam } },
        };
      } else if (selectedRole == 'merchandiser') {
        filter_person['role.code'] = 'merchandiser';
        filter_person.sales_team = {
          $elemMatch: { id: { $in: userSalesTeam } },
        };
      } else {
        filter_person['$and'] = [
          {
            $or: [
              { 'role.code': { $in: ['sales_supervisor', 'merchandiser'] } },
              {
                $and: [
                  { 'role.code': 'salesman' },
                  { sales_code: { $exists: true } },
                ],
              },
            ],
          },
          { sales_team: { $elemMatch: { id: { $in: userSalesTeam } } } },
        ];
      }
    }
    if (
      isSalesman ||
      isBeautyAdvisor ||
      isMerchandiser ||
      isPromotor ||
      isCollector
    ) {
      delete filter_cus.empty;
      filter_cus['$or'] = [
        { 'sales_team.id': { $in: userSalesTeam } },
        { 'market_type.code': 'mt' },
        { 'market_type.code': 'MT' },
      ];
      if (isBeautyAdvisor) {
        filter_cus['beauty_advisor.id'] = userData._id;
      } else if (isMerchandiser) {
        filter_cus['merchandiser.id'] = userData._id;
      }
    }
  } else if (selectedRole == 'salesman') {
    filter_person.remove_default_filter = true;
    filter_person['$or'] = [
      { 'role.code': 'salesman', sales_code: { $exists: true } },
      { 'role.code': 'merchandiser' },
    ];
  } else if (selectedRole == 'beauty_advisor') {
    filter_person.remove_default_filter = true;
    filter_person['role.code'] = 'beauty_advisor';
  } else if (selectedRole == 'merchandiser') {
    filter_person.remove_default_filter = true;
    filter_person['role.code'] = 'merchandiser';
  } else if (selectedRole == 'promotor') {
    filter_person.remove_default_filter = true;
    filter_person['role.code'] = 'promotor';
  } else if (selectedRole == 'collector') {
    filter_person.remove_default_filter = true;
    filter_person['role.code'] = 'collector';
  }

  const FormValidationSchema = Yup.object().shape({
    sales_person: Yup.object()
      .required('Sales person is required.')
      .when('status', (status) => {
        if (status != 'draft') {
          return Yup.object().required('Sales person is required.');
        }
        return Yup.object();
      }),
    customer: Yup.object().required('Customer is required.'),
    date: Yup.string()
      .required('Date is required.')
      .when('status', (status) => {
        if (status != 'draft') {
          return Yup.string().required('Date is required.');
        }
        return Yup.string();
      }),
    start_time: Yup.string()
      .required('Time from is required.')
      .when('status', (status) => {
        if (status != 'draft') {
          return Yup.string().required('Time from is required.');
        }
        return Yup.string();
      }),
    end_time: Yup.string()
      .required('Time to is required.')
      .when('status', (status) => {
        if (status != 'draft') {
          return Yup.string().required('Time to is required.');
        }
        return Yup.string();
      }),
    notes: Yup.string(),
    ...(activityType === 'event_activity'
      ? {
          event_name: Yup.string().when('status', (status) => {
            if (status != 'draft') {
              return Yup.string().required('Event name is required.');
            }
            return Yup.string();
          }),
        }
      : {}),
    ...((activityType === 'visiting_existing_customer' ||
      activityType === 'invoice_collection') &&
    isSubDistributor
      ? {
          outlet: Yup.object().required('Outlet is required.'),
        }
      : {}),
  });

  useEffect(() => {
    let mounted = true;
    setHasDeliveryChanged(false);
    setDisabled(false);
    setIsSubDistributor(false);
    if (mounted && show) {
      setAddresses([]);
      if (detailData?.customer?.id) {
        getCustomer(detailData.customer.id);
      }
    }
    return () => {
      mounted = false;
    };
  }, [show]);

  const mobileSubmit = () => {
    if (formRef.current) {
      formRef.current.setFieldValue('status', 'draft');
      if (!formRef.current.values.sales_person) {
        formRef.current.setFieldValue('sales_person', {});
      }
      setTimeout(() => {
        formRef.current.validateForm().then((validate) => {
          formRef.current.handleSubmit();
        });
      }, 100);
    }
  };

  const resetAddress = () => {
    setAddressType('');
    setListAddress([]);
    setListAddressType({
      store: [],
      delivery: [],
    });
  };

  const needToClose = () => {
    if (isEdit) {
      onClose();
    } else {
      const btnStyle = {
        width: isWeb ? 100 : 220,
        marginHorizontal: isWeb ? 5 : 0,
        marginBottom: 10,
      };
      dispatch(
        showDiscard({
          show: true,
          title: 'Discard',
          description: 'Unsaved data will be deleted. Do you want to proceed?',
          buttons: [
            {
              type: 'main',
              style: btnStyle,
              textColor: Colors.white,
              text: 'OK',
              callback: () => {
                onClose();
                dispatch(hideDiscard());
                resetAddress();
              },
            },
            {
              type: 'default',
              style: btnStyle,
              textColor: Colors.black,
              text: 'Save Draft',
              callback: () => {
                dispatch(hideDiscard());
                mobileSubmit();
                resetAddress();
              },
            },
            {
              type: 'default',
              style: btnStyle,
              textColor: Colors.black,
              text: 'Cancel',
              callback: () => dispatch(hideDiscard()),
            },
          ],
        }),
      );
    }
  };

  const getCustomer = async (id) => {
    if (id) {
      const response = await getCustomerDetail.request(id);
      if (
        response.status == 200 &&
        response?.data?.data &&
        Object.keys(response.data.data).length
      ) {
        const result = response.data.data;
        const marketType = (result?.market_type?.code || '').toLowerCase();
        const deliveryAddresses = (result?.delivery_address || []).map(
          withMongoId,
        );
        const storeAddresses = (result?.store_address || []).map(withMongoId);
        setListAddressType({
          store: storeAddresses,
          delivery: deliveryAddresses,
        });
        if (result && result?.delivery_address?.length && !addresses.length) {
          setAddresses(deliveryAddresses);
        }
        if (formRef?.current?.values) {
          const fallbackAddressType =
            marketType === 'gt'
              ? 'delivery'
              : marketType === 'mt'
              ? 'store'
              : 'delivery';
          const currentAddressId =
            formRef.current.values?.customer_address?._id ||
            formRef.current.values?.customer_address?.id ||
            formRef.current.values?.customer?.address?._id ||
            formRef.current.values?.customer?.address?.id;
          const currentAddressType =
            formRef.current.values?.customer_address?.type ||
            formRef.current.values?.customer?.address?.type;
          const allAddresses = [...deliveryAddresses, ...storeAddresses];

          let matchedAddress = null;
          if (currentAddressId) {
            matchedAddress = allAddresses.find(
              (address) =>
                address._id == currentAddressId ||
                address.id == currentAddressId,
            );
          }
          if (!matchedAddress && currentAddressType) {
            matchedAddress = allAddresses.find((address) => {
              if (!address?.type || !currentAddressType) {
                return false;
              }
              return (
                address.type.toLowerCase() === currentAddressType.toLowerCase()
              );
            });
          }
          if (!matchedAddress && allAddresses.length) {
            matchedAddress = allAddresses[0];
          }

          if (matchedAddress) {
            formRef.current.setFieldValue('customer_address', matchedAddress);
            const nextType =
              (matchedAddress?.type &&
                typeof matchedAddress.type === 'string' &&
                matchedAddress.type) ||
              currentAddressType ||
              fallbackAddressType ||
              'delivery';
            setAddressType(nextType);
          } else if (!addressType) {
            const nextType =
              (currentAddressType && typeof currentAddressType === 'string'
                ? currentAddressType
                : null) ||
              fallbackAddressType ||
              'delivery';
            setAddressType(nextType);
          }
        }

        if (result?.is_sub_distributor) {
          setIsSubDistributor(true);
          try {
            setLoadingCustomer(true);
            const outletsResponse = await getOutlets(result?._id || result?.id);
            if (outletsResponse?.data?.data) {
              const outletData = (outletsResponse.data.data || []).map(
                withMongoId,
              );
              setOutlets(outletData);
              if (formRef?.current?.values?.outlet) {
                const currentOutletId =
                  formRef.current.values.outlet._id ||
                  formRef.current.values.outlet.id;
                if (currentOutletId) {
                  const matchedOutlet = outletData.find(
                    (outlet) =>
                      outlet._id == currentOutletId ||
                      outlet.id == currentOutletId,
                  );
                  if (matchedOutlet) {
                    formRef.current.setFieldValue('outlet', matchedOutlet);
                    formRef.current.setFieldValue(
                      'customer_address',
                      matchedOutlet,
                    );
                    const fallbackOutletType =
                      marketType === 'gt'
                        ? 'delivery'
                        : marketType === 'mt'
                        ? 'store'
                        : 'delivery';
                    const currentType =
                      formRef.current.values.customer_address?.type;
                    const nextType =
                      (matchedOutlet?.type &&
                        typeof matchedOutlet.type === 'string' &&
                        matchedOutlet.type) ||
                      (currentType && typeof currentType === 'string'
                        ? currentType
                        : null) ||
                      fallbackOutletType ||
                      'delivery';
                    setAddressType(nextType);
                  }
                }
              }
            }
          } catch (error) {
          } finally {
            setLoadingCustomer(false);
          }
        } else {
          setIsSubDistributor(false);
        }
      }
    }
  };

  const disabledStartTime = (value) => {
    const startDate = parseDateValue(value);
    if (startDate && startDate.getTime() > today.getTime()) {
      if (startDate.getDate() == new Date().getDate()) {
        return `${startDate.getHours()}:${startDate.getMinutes()}`;
      } else {
        return false;
      }
    }
    return now_time;
  };

  const getDisableAfterTime = (startTime, dateValue) => {
    if (startTime) {
      return startTime;
    }
    const parsedDate = parseDateValue(dateValue);
    if (parsedDate) {
      return parsedDate.getDate() != today.getDate() ? false : now_time;
    }
    return now_time;
  };

  const checkCustomerNameEngage = () => {
    let customer_name = formRef?.current?.values?.customer?.name;
    if (customer_name) {
      customer_name = customer_name.replace(/\s/g, '');
    }
    if (isEngage && !customer_name) {
      formRef.current.setErrors({
        customer: 'Customer is required.',
      });
      return false;
    }
    return true;
  };

  const onLocationChanged = () => {
    setHasDeliveryChanged(true);
    setTimeout(() => {
      setHasDeliveryChanged(false);
    }, 500);
  };

  return (
    <ModalFull
      testID="sales-activity-form-modal"
      title={
        isEdit
          ? `${prefix_title}${
              detailData && detailData.customer && detailData.customer.name
                ? ` - ${detailData.customer.name}`
                : ''
            }`
          : activityType == 'visiting_existing_customer'
          ? 'Visit Existing Customer'
          : prefix_title
      }
      show={show}
      back={isWeb ? false : true}
      onClose={() => (isLoading ? null : needToClose())}
      withMobileMenu={true}
      closeOutside={false}
      mobileMenu={[
        {
          name: 'Save Draft',
          callback: () => (isLoading ? null : mobileSubmit()),
        },
      ]}>
      {(isEdit ? detailData && Object.keys(detailData).length : true) ? (
        <Formik
          innerRef={formRef}
          initialValues={initialValues}
          validationSchema={FormValidationSchema}
          onSubmit={async (values) => {
            if (submitLockRef.current) {
              return;
            }
            submitLockRef.current = true;
            let isError = false;
            if (values?.status != 'draft') {
              if (isEngage ? !values?.customer?.name : !values?.customer?.id) {
                if (formRef.current) {
                  formRef.current.setFieldError(
                    'customer',
                    'Customer is required',
                  );
                  isError = true;
                }
              }

              if (!isEngage && !values?.customer_address?._id) {
                if (formRef.current) {
                  formRef.current.setFieldError(
                    'customer_address',
                    'Customer address is required',
                  );
                  isError = true;
                }
              }
            }

            if (isError) {
              submitLockRef.current = false;
              return;
            }
            dispatch(showPopup({ name: 'isLoading', value: true }));
            setIsLoading(true);
            setDisabled(true);
            const checkCustomer = checkCustomerNameEngage();
            if (!checkCustomer) {
              dispatch(showPopup({ name: 'isLoading', value: false }));
              setIsLoading(false);
              setDisabled(false);
              submitLockRef.current = false;
              return false;
            }
            const status = values.status;
            const message = {
              title: 'New Activity Plan Submitted',
              description:
                'New activity plan submitted, you`ll be notified once it has been approved.',
            };
            if (status == 'draft') {
              message.title = 'Activity Plan Saved as Draft';
              message.description =
                'New activity plan saved as draft, you can continue to complete and submit data later.';
            } else {
              if (
                [
                  'sales_supervisor',
                  'branch_head',
                  'regional_manager',
                  'regional_support',
                  'key_account_manager',
                  'group_key_account_manager',
                  'national_key_account_manager',
                ].includes(userRole)
              ) {
                message.description = 'New activity plan submitted.';
              } else {
                const additional_description =
                  userRole == 'salesman'
                    ? ', you`ll be notified once it has been approved'
                    : '';
                message.description = `New activity plan submitted${additional_description}.`;
              }
            }

            try {
              const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
              const normalizedDateValue = parseDateValue(values.date);
              const payload = {
                title: `${prefix_title}${
                  values.customer && values.customer.name
                    ? ` - ${values.customer.name}`
                    : ''
                }`,
                customer:
                  !isEngage && values?.customer?.id
                    ? {
                        id:
                          values.customer && values.customer.id
                            ? values.customer.id
                            : '',
                        name:
                          values.customer && values.customer.name
                            ? values.customer.name
                            : '',
                        address:
                          values.customer_address &&
                          Object.keys(values.customer_address).length
                            ? {
                                id: values.customer_address._id,
                                name: values.customer_address.name,
                                street: values.customer_address.street,
                                street2: values.customer_address.street2,
                                postal_code:
                                  values.customer_address.postal_code,
                                country: values.customer_address.country,
                                state: values.customer_address.state,
                                city: values.customer_address.city,
                                lat_long: values.customer_address.lat_long,
                                is_premium: values.customer_address.is_premium,
                              }
                            : '',
                      }
                    : values.customer,
                user:
                  values.sales_person && values.sales_person.id
                    ? {
                        id:
                          values.sales_person && values.sales_person.id
                            ? values.sales_person.id
                            : '',
                        name:
                          values.sales_person && values.sales_person.name
                            ? values.sales_person.name
                            : '',
                        ...(values.sales_person?.role
                          ? {
                              role:
                                typeof values.sales_person.role === 'object'
                                  ? { ...values.sales_person.role }
                                  : values.sales_person.role,
                            }
                          : userData.role?.id
                          ? { role: userData.role?.code }
                          : {}),
                      }
                    : '',
                activity_type: activityType,
                notes: values.notes ? values.notes : '',
                ...(activityType === 'event_activity' && values.event_name
                  ? { event_name: values.event_name }
                  : {}),
                visited_at: normalizedDateValue
                  ? convertTimeOfDate(
                      normalizedDateValue,
                      values.start_time,
                      tz,
                    )
                  : '',
                start_time: values.start_time ? values.start_time : '',
                end_time: values.end_time ? values.end_time : '',
                status: status,
                ...(isSubDistributor
                  ? values.outlet
                    ? {
                        outlet: {
                          id: values.outlet._id,
                          name: values.outlet.name,
                          address: {
                            id: values.outlet._id,
                            name: values.outlet.name,
                            street: values.outlet.street,
                            country: values.outlet.country,
                            state: values.outlet.state,
                            city: values.outlet.city,
                            lat_long: values.outlet.lat_long,
                          },
                        },
                      }
                    : {}
                  : {}),
              };
              const normalizedAddressType = isSubDistributor
                ? 'outlet'
                : (addressType && typeof addressType === 'string'
                    ? addressType
                    : null) ||
                  (values?.customer_address?.type &&
                  typeof values.customer_address.type === 'string'
                    ? values.customer_address.type
                    : null) ||
                  'delivery';

              payload.customer.address = {
                ...payload.customer.address,
                type: normalizedAddressType,
              };
              if (isEdit) {
                let updateData = null;
                if (payload.status == 'waiting_confirmation') {
                  updateData = await MsB2b.updateSalesActivityStatus(
                    detailData._id,
                    payload.status,
                    payload,
                  );
                } else {
                  payload.edit_draft = true;
                  updateData = await MsB2b.updateSalesActivity(
                    detailData._id,
                    payload,
                  );
                }
                if (updateData?.data?.data) {
                  dispatch(showPopup({ name: 'isLoading', value: false }));
                  setIsLoading(false);
                  setDisabled(false);
                  onClose();
                  dispatch(
                    showAlert({
                      show: true,
                      isModal: true,
                      title: message.title,
                      description: message.description,
                      isConfirm: false,
                      testID: 'sales-activity-form-update-success',
                      onCallback: () => {
                        dispatch(hideAlert());
                        onSubmit();
                      },
                    }),
                  );
                } else {
                  dispatch(showPopup({ name: 'isLoading', value: false }));
                  setIsLoading(false);
                  setDisabled(false);
                  dispatch(
                    showAlert({
                      show: true,
                      isModal: true,
                      title: 'Error',
                      description: 'A system error occurred',
                      isConfirm: false,
                      testID: 'sales-activity-form-update-error',
                      onCallback: () => {
                        dispatch(hideAlert());
                      },
                    }),
                  );
                }
              } else {
                const createData = await MsB2b.createSalesActivity(payload);
                if (createData?.data?.data) {
                  dispatch(showPopup({ name: 'isLoading', value: false }));
                  setIsLoading(false);
                  onClose();
                  dispatch(
                    showAlert({
                      show: true,
                      isModal: true,
                      title: message.title,
                      description: message.description,
                      isConfirm: false,
                      testID: 'sales-activity-form-create-success',
                      onCallback: () => {
                        dispatch(hideAlert());
                        onSubmit();
                      },
                    }),
                  );
                } else {
                  dispatch(showPopup({ name: 'isLoading', value: false }));
                  setIsLoading(false);
                  setDisabled(false);
                  dispatch(
                    showAlert({
                      show: true,
                      isModal: true,
                      title: 'Error',
                      description: 'A system error occurred',
                      isConfirm: false,
                      testID: 'sales-activity-form-create-error',
                      onCallback: () => {
                        dispatch(hideAlert());
                      },
                    }),
                  );
                }
              }

              resetAddress();
            } catch (error) {
              dispatch(showPopup({ name: 'isLoading', value: false }));
              setIsLoading(false);
              setDisabled(false);
              dispatch(
                showAlert({
                  show: true,
                  isModal: true,
                  title: 'Error',
                  description:
                    error?.response?.data?.message || 'A system error occurred',
                  isConfirm: false,
                  testID: 'sales-activity-form-error',
                  onCallback: () => {
                    dispatch(hideAlert());
                  },
                }),
              );
            } finally {
              submitLockRef.current = false;
            }
          }}>
          {({
            handleChange,
            setFieldValue,
            validateForm,
            handleSubmit,
            errors,
            touched,
            values,
          }) => (
            <KeyboardWrapper
              style={
                isWeb
                  ? { maxHeight: dimension.height - 300 }
                  : isMobileWeb
                  ? {
                      maxHeight:
                        dimension.height - insets.top - insets.bottom - 55,
                    }
                  : {}
              }
              keyboardVerticalOffset={100}
              contentContainerStyle={{ width: '100%', padding: 15 }}>
              {isLoading ? <FullLoader show={true} /> : false}
              <FormWrapper>
                <View
                  style={{
                    width: '100%',
                    flexDirection: isWeb ? 'row' : 'column',
                    justifyContent: 'space-between',
                  }}>
                  <View
                    style={{
                      width: isWeb ? '50%' : '100%',
                      paddingRight: isWeb ? 15 : 0,
                      borderRightColor: isWeb ? Colors.grey0 : 'transparent',
                      borderRightWidth: 1,
                    }}>
                    <FormGroup>
                      <SalesPersonSearch
                        testID="sales-activity-form-sales-person"
                        label={roleLabel}
                        placeholder={'Select ' + roleLabel}
                        value={values.sales_person}
                        isRequired={true}
                        hasError={
                          errors.sales_person && touched.sales_person
                            ? errors.sales_person
                            : null
                        }
                        editable={
                          !isSalesman &&
                          !isBeautyAdvisor &&
                          !isMerchandiser &&
                          !isPromotor &&
                          !isCollector &&
                          !isEdit
                        }
                        notFetch={
                          isSalesman ||
                          isBeautyAdvisor ||
                          isMerchandiser ||
                          isPromotor ||
                          isCollector
                        }
                        customFilter={filter_person}
                        onChange={(value) => {
                          setFieldValue('customer', '');
                          setFieldValue('customer_address', '');
                          setFieldValue('outlet', '');
                          setFieldValue('customer_address', '');
                          setFieldValue('sales_person', '');
                          setFieldValue('sales_person', value);
                          const newSalesTeam = extractIds(value?.sales_team);
                          if (newSalesTeam?.length) {
                            setFilterCustomer(newSalesTeam);
                          } else {
                            setFilterCustomer([]);
                          }
                          setLoadingCustomer(true);
                          setTimeout(() => {
                            setLoadingCustomer(false);
                          }, 500);
                        }}
                      />
                    </FormGroup>
                    {activityType === 'event_activity' ? (
                      <FormGroup>
                        <Input
                          testID="sales-activity-form-event-name"
                          label="Event Name"
                          isRequired={values.status !== 'draft'}
                          placeholder="Enter event name"
                          value={values.event_name}
                          hasError={
                            errors.event_name && touched.event_name
                              ? errors.event_name
                              : null
                          }
                          onChangeText={(value) =>
                            setFieldValue('event_name', value)
                          }
                        />
                      </FormGroup>
                    ) : null}
                    <FormGroup>
                      {isEngage ? (
                        <Input
                          testID="sales-activity-form-customer-input"
                          label="Customer"
                          value={
                            values.customer?.name ? values.customer.name : ''
                          }
                          hasError={
                            errors.customer && touched.customer
                              ? errors.customer
                              : null
                          }
                          onChangeText={(value) =>
                            setFieldValue('customer', { name: value })
                          }
                        />
                      ) : !loadingCustomer ? (
                        <CustomerSearch
                          fields="_id name market_type is_sub_distributor store_address delivery_address"
                          testID="sales-activity-form-customer"
                          value={values.customer}
                          isRequired={true}
                          hasError={
                            errors.customer && touched.customer
                              ? errors.customer
                              : null
                          }
                          loading={loadingCustomer}
                          customFilter={filter_cus}
                          editable={!isEdit}
                          onChange={async (value) => {
                            setAddressType('');
                            setListAddressType({ delivery: [], store: [] });
                            setListAddress([]);
                            setFieldValue('customer', '');
                            setFieldValue('customer_address', '');
                            setFieldValue('outlet', '');
                            setIsSubDistributor(false);
                            setFieldValue('customer', value);
                            if (value?.is_sub_distributor) {
                              if (value?._id) {
                                try {
                                  setLoadingCustomer(true);
                                  const outlets = await getOutlets(value?._id);
                                  if (outlets?.data?.data) {
                                    const data = (
                                      outlets?.data?.data || []
                                    ).map((val) => {
                                      return {
                                        ...val,
                                        _id: val._id,
                                        name: val.name,
                                      };
                                    });
                                    setOutlets(data);
                                  }
                                  setIsSubDistributor(true);
                                } catch (error) {
                                } finally {
                                  setLoadingCustomer(false);
                                }
                              }
                            } else {
                              setIsSubDistributor(false);
                              setOutlets([]);
                              setFieldValue('outlet', '');
                            }
                            setListAddressType({
                              store: value?.store_address || [],
                              delivery: value?.delivery_address || [],
                            });
                            if (value?.market_type?.code) {
                              const marketType =
                                value.market_type.code.toLowerCase();
                              const addressKey =
                                marketType === 'gt' ? 'delivery' : 'store';
                              const addressList =
                                addressKey === 'delivery'
                                  ? value.delivery_address
                                  : value.store_address;
                              const showAddress = Array.isArray(addressList)
                                ? addressList
                                : [];

                              setFieldValue(
                                'customer_address',
                                showAddress[0] || '',
                              );
                              setAddressType(addressKey);
                              if (showAddress.length > 0) {
                                onLocationChanged();
                              }
                            }
                          }}
                        />
                      ) : (
                        <InputLoading label="Customer" isRequired />
                      )}
                    </FormGroup>
                    {!isEngage ? (
                      <>
                        {isSubDistributor && !loadingCustomer && (
                          <FormGroup>
                            <SelectBox
                              testID="sales-activity-form-outlet"
                              label="Outlet Name"
                              options={filteredOutlets}
                              value={values.outlet}
                              placeholder="Select Outlet Name"
                              trackBy="_id"
                              labelKey="name"
                              isRequired={true}
                              hasError={
                                errors.outlet && touched.outlet
                                  ? errors.outlet
                                  : null
                              }
                              withCallback={isVn()}
                              searchCallback={
                                isVn()
                                  ? (val) => setOutletSearchKeyword(val || '')
                                  : null
                              }
                              editable={
                                !isEdit ||
                                !values.status ||
                                values.status === 'draft'
                              }
                              onChange={(value) => {
                                setOutletSearchKeyword('');
                                setFieldValue('customer_address', value);
                                setFieldValue('outlet', value);
                                onLocationChanged();
                              }}
                            />
                          </FormGroup>
                        )}
                        {!isSubDistributor && (
                          <FormGroup>
                            <SociollaText
                              style={{
                                fontSize: 11,
                                marginBottom: 10,
                                marginTop: 10,
                              }}>
                              Customer Address Type
                            </SociollaText>
                            <RadioButton
                              testID="sales-activity-form-address-type"
                              orientation="horizontal"
                              value={addressType}
                              data={addressTypeOptions}
                              onChange={(value) => {
                                const showAddress = Array.isArray(
                                  listAddressType[value],
                                )
                                  ? listAddressType[value]
                                  : [];

                                setAddressType(value);

                                if (showAddress.length > 0) {
                                  setFieldValue(
                                    'customer_address',
                                    showAddress[0],
                                  );
                                } else {
                                  setFieldValue('customer_address', '');
                                }
                              }}
                            />
                          </FormGroup>
                        )}
                        {!isSubDistributor && (
                          <FormGroup>
                            <SelectBox
                              testID="sales-activity-form-customer-address"
                              label="Customer Address"
                              options={filteredListAddress}
                              value={values.customer_address}
                              placeholder="Select Customer Address"
                              trackBy="_id"
                              labelKey="name"
                              isRequired={true}
                              hasError={
                                errors.customer_address
                                  ? errors.customer_address
                                  : null
                              }
                              editable={
                                (!isEdit ||
                                  !values.status ||
                                  values.status === 'draft') &&
                                values.customer?.name !== '' &&
                                values.customer?.name !== null &&
                                values.customer?.name !== undefined
                              }
                              withCallback={isVn()}
                              searchCallback={
                                isVn()
                                  ? (val) => setAddressSearchKeyword(val || '')
                                  : null
                              }
                              onChange={(value) => {
                                setAddressSearchKeyword('');
                                setFieldValue('customer_address', value);
                                onLocationChanged();
                              }}
                            />
                          </FormGroup>
                        )}
                        <FormGroup>
                          <View
                            style={{
                              width: '100%',
                              flexDirection: 'row',
                              marginTop: 15,
                            }}>
                            <SociollaText
                              style={{
                                minWidth: 50,
                                fontSize: 13,
                                marginTop: 2,
                                color: Colors.blue0,
                              }}>
                              Detail :{' '}
                            </SociollaText>
                            <SociollaText
                              style={{
                                fontSize: 13,
                                flexShrink: 1,
                                lineHeight: 18,
                              }}>
                              {values.customer_address
                                ? convertFullAddress(
                                    values.customer_address,
                                    true,
                                  )
                                : '-'}
                            </SociollaText>
                          </View>
                        </FormGroup>
                        {!hasDeliveryChanged &&
                        values.customer_address?.lat_long?.latitude ? (
                          <FormGroup>
                            <View
                              style={{
                                width: '100%',
                                flexDirection: 'row',
                                marginTop: 15,
                              }}>
                              <MapPicker
                                value={values.customer_address.lat_long}
                                hideInput={true}
                                disabled={true}
                                editable={false}
                                containerStyle={{
                                  marginTop: 14,
                                  borderRadius: 4,
                                  width: '100%',
                                }}
                              />
                            </View>
                          </FormGroup>
                        ) : (
                          false
                        )}
                      </>
                    ) : (
                      false
                    )}
                  </View>
                  <View
                    style={{
                      width: isWeb ? '50%' : '100%',
                      paddingLeft: isWeb ? 15 : 0,
                    }}>
                    <FormGroup>
                      <DatePicker
                        testID="sales-activity-form-date"
                        label="Date"
                        name="date"
                        disabled={!values.customer?.name}
                        disablePastDates={true}
                        value={values.date}
                        hasError={
                          errors.date && touched.date ? errors.date : null
                        }
                        onChange={(value) => setFieldValue('date', value)}
                      />
                    </FormGroup>
                    <FormGroup>
                      <SociollaText style={{ fontSize: 12 }}>Time</SociollaText>
                      <View
                        style={{
                          width: '100%',
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                        }}>
                        <View style={{ width: '48%' }}>
                          <TimePicker
                            testID="sales-activity-form-time-from"
                            label="From"
                            value={values.start_time}
                            disableBeforeTime={values.end_time}
                            disableAfterTime={disabledStartTime(values.date)}
                            hasError={
                              errors.start_time && touched.start_time
                                ? errors.start_time
                                : null
                            }
                            onChange={(value) =>
                              setFieldValue('start_time', value)
                            }
                          />
                        </View>
                        <View style={{ width: '48%' }}>
                          <TimePicker
                            testID="sales-activity-form-time-to"
                            label="To"
                            value={values.end_time}
                            disableAfterTime={getDisableAfterTime(
                              values.start_time,
                              values.date,
                            )}
                            hasError={
                              errors.end_time && touched.end_time
                                ? errors.end_time
                                : null
                            }
                            onChange={(value) =>
                              setFieldValue('end_time', value)
                            }
                          />
                        </View>
                      </View>
                    </FormGroup>
                    <FormGroup>
                      <Input
                        testID="sales-activity-form-notes"
                        label="Notes (Optional)"
                        value={values.notes}
                        multiline={true}
                        numberOfLines={4}
                        hasError={
                          errors.notes && touched.notes ? errors.notes : null
                        }
                        onChangeText={handleChange('notes')}
                      />
                    </FormGroup>
                    {isWeb ? (
                      <View style={{ paddingHorizontal: 10, marginTop: 10 }}>
                        <Button
                          testID="sales-activity-form-save-draft-btn"
                          type="primary"
                          rounded={true}
                          buttonStyle={{ width: '100%', marginBottom: 10 }}
                          title={
                            <SociollaText
                              type="bold"
                              style={{
                                fontSize: 14,
                                letterSpacing: 0.5,
                                color: Colors.white,
                              }}>
                              Save Draft
                            </SociollaText>
                          }
                          onPress={(evt) => {
                            evt.persist();
                            setFieldValue('status', 'draft');
                            if (!values.sales_person) {
                              setFieldValue('sales_person', {});
                            }
                            setTimeout(() => {
                              validateForm().then((validate) => {
                                handleSubmit(evt);
                              });
                            }, 100);
                          }}
                        />
                        <Button
                          testID="sales-activity-form-submit-web-btn"
                          rounded={true}
                          buttonStyle={{ width: '100%' }}
                          disabled={disabled}
                          title={
                            <SociollaText
                              type="bold"
                              style={{
                                fontSize: 14,
                                letterSpacing: 0.5,
                                color: Colors.white,
                              }}>
                              Submit
                            </SociollaText>
                          }
                          onPress={(evt) => {
                            evt.persist();
                            setFieldValue('status', 'waiting_confirmation');
                            setTimeout(() => {
                              validateForm().then((validate) => {
                                handleSubmit(evt);
                              });
                            }, 100);
                          }}
                        />
                      </View>
                    ) : (
                      false
                    )}
                  </View>
                </View>
              </FormWrapper>
              <View
                style={{
                  width: '100%',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                  paddingVertical: 15,
                }}>
                {!isWeb ? (
                  <Button
                    testID="sales-activity-form-submit-btn"
                    rounded={true}
                    buttonStyle={{
                      width: isWeb ? 150 : dimension.width - 40,
                    }}
                    disabledStyle={{
                      width: isWeb ? 150 : dimension.width - 40,
                    }}
                    disabled={disabled}
                    title={
                      <SociollaText
                        type="bold"
                        style={{
                          fontSize: 14,
                          letterSpacing: 0.5,
                          color: Colors.white,
                        }}>
                        {isLoading ? 'Loading' : 'Submit'}
                      </SociollaText>
                    }
                    onPress={(evt) => {
                      evt.persist();
                      setFieldValue('status', 'waiting_confirmation');
                      setTimeout(() => {
                        validateForm().then((validate) => {
                          handleSubmit(evt);
                        });
                      }, 100);
                    }}
                  />
                ) : (
                  false
                )}
              </View>
            </KeyboardWrapper>
          )}
        </Formik>
      ) : (
        <Loader />
      )}
      <Alert />
      <Discard />
    </ModalFull>
  );
};

export default React.memo(SalesActivityForm, (prev, next) => prev == next);
